import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListFilter } from 'lucide-react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';

interface FilterPopoverProps {
  /** Number of active (non-default) filters — shown as a badge on the button. */
  activeCount: number;
  onReset: () => void;
  children: React.ReactNode;
}

const PANEL_WIDTH = 240; // w-60

/**
 * A compact "Filters" button that opens a dropdown panel with filter controls.
 * The panel renders in a body portal with fixed positioning so it is never
 * clipped by overflow containers. Closes on outside click, Escape, or scroll.
 */
export function FilterPopover({ activeCount, onReset, children }: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const close = () => setOpen(false);

  const toggle = () => {
    if (open) return close();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)),
        top: rect.bottom + 4,
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <div ref={triggerRef} className="relative shrink-0">
      <Button
        variant={activeCount > 0 ? 'secondary' : 'outline'}
        size="sm"
        className="h-8 text-xs"
        onClick={toggle}
        aria-expanded={open}
      >
        <ListFilter className="h-3.5 w-3.5 mr-1.5" />
        Filters
        {activeCount > 0 && (
          <Badge variant="default" className="ml-1.5 h-4 min-w-4 px-1 text-[10px] justify-center">
            {activeCount}
          </Badge>
        )}
      </Button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            style={{ left: pos.left, top: pos.top, width: PANEL_WIDTH }}
            className="fixed z-[100] rounded-lg border bg-popover p-3 shadow-pop animate-scale-in space-y-3"
          >
            {children}
            {activeCount > 0 && (
              <button type="button" onClick={onReset} className="text-[11px] text-primary hover:underline">
                Reset filters
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
